import { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useUploadTradingCsv } from "../useUploadTradingCsv";

const Trading212CsvUploadPage = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const uploadMutation = useUploadTradingCsv();

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        setSelectedFile(file);
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    
    uploadMutation.mutate(selectedFile, {
      onSuccess: (data) => {
        setShowSuccessSnackbar(true);
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById("file-input") as HTMLInputElement;
        if (fileInput) fileInput.value = "";
      },
      onError: (error) => {
        setErrorMessage(error.error ?? "Upload failed. Please try again.");
        setShowErrorSnackbar(true);
      },
    });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate("/portfolio");
          }}
          sx={{ cursor: "pointer" }}
        >
          Portfolio
        </Link>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate("/portfolio/trading212");
          }}
          sx={{ cursor: "pointer" }}
        >
          Trading212
        </Link>
        <Typography color="text.primary">CSV Upload</Typography>
      </Breadcrumbs>

      <Typography variant="h4" component="h1" gutterBottom>
        Trading212 CSV Upload
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Upload your Trading212 CSV export to import your portfolio transaction history and analyze your investments.
      </Typography>

      <Paper
        sx={{
          p: 4,
          mt: 3,
          border: `2px dashed ${dragActive ? "#1976d2" : "#e0e0e0"}`,
          borderRadius: 2,
          backgroundColor: dragActive ? "#f5f5f5" : "inherit",
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: "#1976d2",
            backgroundColor: "#f5f5f5",
          },
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById("file-input")?.click()}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <UploadIcon sx={{ fontSize: 48, color: "text.secondary" }} />
          <Typography variant="h6" color="text.secondary">
            {selectedFile
              ? `Selected: ${selectedFile.name}`
              : "Drag and drop your Trading212 CSV file here, or click to browse"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Only CSV files are supported • Maximum file size: 50MB
          </Typography>
        </Box>
      </Paper>

      <input
        id="file-input"
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>How to export from Trading212:</strong>
          <br />
          1. Log into your Trading212 account
          <br />
          2. Navigate to History → Transactions
          <br />
          3. Select the date range you want to export
          <br />
          4. Click "Export" and choose CSV format
          <br />
          5. Download the file and upload it here
        </Typography>
      </Alert>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          size="large"
          disabled={!selectedFile || uploadMutation.isPending}
          onClick={handleUpload}
          startIcon={
            uploadMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <UploadIcon />
            )
          }
          sx={{ px: 4 }}
        >
          {uploadMutation.isPending ? "Uploading..." : "Upload Portfolio Data"}
        </Button>
      </Box>

      <Box 
        sx={{ 
          mt: 4, 
          p: 3, 
          backgroundColor: "rgba(0, 0, 0, 0.04)", 
          borderRadius: 2,
          border: "1px solid rgba(0, 0, 0, 0.12)"
        }}
      >
        <Typography variant="h6" gutterBottom>
          What happens after upload?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Your transaction data will be securely processed and stored<br />
          • Portfolio performance and analytics will be calculated<br />
          • You'll be able to view insights and track your investments<br />
          • Data is securely stored and only accessible to your account
        </Typography>
      </Box>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
      >
        <Alert 
          onClose={() => setShowSuccessSnackbar(false)} 
          severity="success"
          sx={{ width: "100%" }}
        >
          Portfolio data uploaded successfully! Your transactions are now being processed.
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
      >
        <Alert 
          onClose={() => setShowErrorSnackbar(false)} 
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Trading212CsvUploadPage;